import { useState } from "react";

import { LOGO_EXTENSIONS } from "@/lib/brand-assets";
import { cn } from "@/lib/utils";

interface BrandLogoProps {
  /** Fallback text shown when no logo is present or every candidate fails to load. */
  name: string;
  /** Sizing classes: set the height (e.g. `h-10`), width scales with the aspect ratio. */
  className?: string;
}

/**
 * Renders the client brand logo from `/brand/logo.<ext>`, falling back to the
 * project name as text when no candidate loads (so un-branded scaffolds
 * still look right). Personalization (`_download_brand_logo` in scaffold-ui)
 * saves the client's logo in whatever format it was served in -- svg, png or
 * jpg, not always svg -- so this tries each extension in turn instead of
 * assuming svg. Constrained by height so wide wordmarks, not just square
 * icons, render correctly.
 */
export function BrandLogo({ name, className }: BrandLogoProps) {
  const [attempt, setAttempt] = useState(0);

  if (attempt >= LOGO_EXTENSIONS.length) {
    return (
      <span className={cn("font-heading text-foreground font-medium", className)}>{name}</span>
    );
  }

  return (
    <img
      key={attempt}
      src={`/brand/logo.${LOGO_EXTENSIONS[attempt]}`}
      alt={name}
      className={cn("w-auto object-contain", className)}
      onError={() => setAttempt((a) => a + 1)}
    />
  );
}
